import React, { Component } from 'react';
import { PriceType, RoMaxPrice } from '~/Api/User/ProductGroup/RoMaxPrice';
import { formatCountriesError } from '~/UI';

import styles from './styles.less';
import { Typography } from '~/libs/ui/atoms/Typography';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import { calculatePrice } from '../calculatePrice';
import { ProductValues } from '../ProductsTable/ProductsTable';
import { CountrySourceWithTooltip } from '~/libs/ui/molecules/CountrySourceWithTooltip';

interface Props {
	roMaxPrice: RoMaxPrice;
	productValues: { [id: string]: ProductValues };
	referencePackageSize: number | null;
	formatPercentage(percentage: number): string;
}

export default class Calculation extends Component<Props> {
	// eslint-disable-next-line react/sort-comp
	public render() {
		const {
			roMaxPrice: { finalCalculation, products },
		} = this.props;

		return (
			<div className={styles.container}>
				{finalCalculation.emptyResultReason.length > 0 ? (
					<div className='mb-4'>
						<p>{this.renderEmptyResultReason()}</p>
					</div>
				) : null}
				<div className={styles.columns}>
					<div className={styles.leftColumn}>
						<Typography level={5} color='black'>
							Applicable ref. prices:
						</Typography>
						<div className='divide-y divide-gray-200'>
							{finalCalculation.publicCatalogPrice?.fromProducts.map((product, i) => (
								<div
									className='w-full flex flex-nowrap items-center justify-between py-2'
									data-product-id={product.id}
									data-testid={`lowest-exfactory-price-${i}`}
									key={product.id}
								>
									<div className='w-0 max-w-32_ grow shrink flex flex-nowrap items-center justify-start'>
										<span className='w-6 grow-0 shrink-0 text-gray-500 text-left'>{i + 1}.</span>
										<CountrySourceWithTooltip
											countryAlpha={product.dataSource.country.alpha2}
											countryName={product.dataSource.country.name}
											dataSourceShortName={product.dataSource.shortName}
											dataSourceDescription={product.dataSource.description}
										/>
									</div>
									<div className='block pl-2 grow-0 shrink-0 text-[14px] leading-tight tracking-wider'>
										{this.renderCalculatedPrice(products.filter((x) => x.product.id === product.id)[0].product.id)}
									</div>
								</div>
							))}
						</div>
					</div>
					<div className={styles.centerColumn}>
						<Typography level={5} color='black' className='mb-2'>
							Calculated max price:
						</Typography>
						<div className={styles.row}>
							<p className={styles.rowName}>
								Price for CANAMED:
								{finalCalculation.canamedPrice !== null && (
									<span className='block text-[12px] text-gray-500 leading-tight'>
										({this.renderPriceType(finalCalculation.canamedPrice.priceType)})
									</span>
								)}
							</p>
							<p className={styles.rowValue}>
								{finalCalculation.canamedPrice !== null ? (
									<strong>{finalCalculation.canamedPrice.price.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>

						<div className={styles.row}>
							<p className={styles.rowName}>
								Price for Public Catalog:
								{finalCalculation.publicCatalogPrice !== null && (
									<span className='block text-[12px] text-gray-500 leading-tight'>
										({this.renderPriceType(finalCalculation.publicCatalogPrice.priceType)})
									</span>
								)}
							</p>
							<p className={styles.rowValue}>
								{finalCalculation.publicCatalogPrice !== null ? (
									<strong>{finalCalculation.publicCatalogPrice.price.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>

						<div className={styles.row}>
							<p className={styles.rowName}>Generic reference price (65%):</p>
							<p className={styles.rowValue}>
								{finalCalculation.genericReferencePrice !== null ? (
									<strong>{finalCalculation.genericReferencePrice.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>

						<div className={styles.row}>
							<p className={styles.rowName}>Biosimilars reference price (80%):</p>
							<p className={styles.rowValue}>
								{finalCalculation.biosimilarReferencePrice !== null ? (
									<strong>{finalCalculation.biosimilarReferencePrice.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>
					</div>
					<div className={styles.rightColumn}>
						<Typography level={5} className='mb-2'>
							&nbsp;
						</Typography>
						<div className={styles.row}>
							<p className={styles.smallRowName}>
								Current ex-factory price:
								<span className='block text-[12px] text-gray-500 leading-tight'>
									{finalCalculation.referencedProduct.canamed?.dataSource.shortName ?? '(CANAMED)'}
								</span>
							</p>
							<p className={styles.smallRowValue}>
								<strong>
									{finalCalculation.referencedProduct.canamed?.currentExFactoryPrice.roundedHumanFormat()}
								</strong>
							</p>
						</div>

						<div className={styles.row}>
							<p className={styles.smallRowName}>
								Current ex-factory price:
								<span className='block text-[12px] text-gray-500 leading-tight'>
									{finalCalculation.referencedProduct.publicCatalog?.dataSource.shortName ?? '(Public Catalog)'}
								</span>
							</p>
							<p className={styles.smallRowValue}>
								<strong>
									{finalCalculation.referencedProduct.publicCatalog?.currentExFactoryPrice.roundedHumanFormat()}
								</strong>
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	private renderEmptyResultReason() {
		const {
			roMaxPrice: { finalCalculation },
		} = this.props;
		const countryError = finalCalculation.findMaxOneReferenceProductError();

		if (finalCalculation.hasMissingProductSettings()) {
			return <strong className={styles.rowError}>Some products settings are missing</strong>;
		}
		if (finalCalculation.requiresMinOneReferenceProductErrorCode()) {
			return <strong className={styles.rowError}>Calculation requires at least one reference country product</strong>;
		}
		if (countryError !== null) {
			return (
				<strong className={styles.rowError}>
					Calculation requires at most one product for each reference country
					{formatCountriesError(countryError.duplicateCountries)}
				</strong>
			);
		}
		if (finalCalculation.isMissingReferenceSettings()) {
			return <strong className={styles.rowError}>Calculation requires reference settings</strong>;
		}
		if (finalCalculation.isIllegalProductPriceTypeSelected()) {
			return <strong className={styles.rowError}>Some products have illegal price selected</strong>;
		}
		if (finalCalculation.requiresMaxOneProductPerDataSource()) {
			return (
				<strong className={styles.rowError}>
					Calculation requires at most one Romanian product for each data source
				</strong>
			);
		}
		return null;
	}

	renderCalculatedPrice(id: string) {
		const { roMaxPrice, productValues, referencePackageSize } = this.props;

		const packageSize = productValues[id].packageSize;

		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;

		if (parsedPackageSize === null) {
			return null;
		}

		const referencePrice = roMaxPrice.products
			.find((x) => x.product.id === id)
			?.referencePrices.find((x) => x.calculatedPrice.selected);

		if (!referencePrice) return;
		return calculatePrice(
			referencePrice.calculatedPrice.price,
			parsedPackageSize,
			referencePackageSize
		).roundedHumanFormat();
	}

	private renderPriceType(priceType: PriceType) {
		switch (priceType) {
			case PriceType.essentialMax:
				return 'approved price in Canamed + 20%';
			case PriceType.fxUpdatePrice:
				return 'FX Update';
			case PriceType.prgPrbPrice:
				return 'PRG/PRB';
			case PriceType.priPrice:
				return 'PRI';
			case PriceType.listAvgOf3Min:
				return 'average of 3 lowest ex-factory prices';
			case PriceType.listMin:
				return 'lowest ex-factory price';
			default:
				return '';
		}
	}
}
